import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';

const MaternityZenPage = () => {
  const services = [
    {
      icon: 'ri-hand-heart-line',
      title: 'Massage prénatal',
      description: 'Massages doux et sécuritaires adaptés à chaque trimestre de grossesse pour soulager les tensions et favoriser la détente.'
    },
    {
      icon: 'ri-run-line',
      title: 'Gymnastique douce',
      description: 'Exercices adaptés pour maintenir votre forme physique, préparer votre corps à l\'accouchement et renforcer votre périnée.'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Gestion du stress',
      description: 'Techniques de relaxation et de respiration pour gérer l\'anxiété et vous préparer mentalement à l\'arrivée de bébé.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Sécurité maman & bébé',
      description: 'Tous nos protocoles sont validés médicalement pour garantir la sécurité et le bien-être de la maman et du bébé.'
    }
  ];

  const benefits = [
    'Soulagement des douleurs dorsales et lombaires',
    'Réduction des œdèmes et de la rétention d\'eau',
    'Amélioration de la circulation sanguine',
    'Diminution du stress et de l\'anxiété',
    'Meilleure qualité de sommeil',
    'Préparation physique et mentale à l\'accouchement',
    'Renforcement du lien maman-bébé',
    'Accompagnement personnalisé tout au long de la grossesse'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-pink-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center space-x-2 bg-pink-100 px-4 py-2 rounded-full">
                <i className="ri-parent-line text-pink-600"></i>
                <span className="text-sm font-medium text-pink-600">Spécial Grossesse</span>
              </div>
              <h1 className="font-serif text-5xl lg:text-6xl font-bold text-gray-900">
                Maternity Zen
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Un accompagnement complet et bienveillant pour vivre votre grossesse en toute sérénité à Lubumbashi
              </p>
              <ul className="space-y-3">
                {['Massage prénatal sécuritaire', 'Gymnastique douce adaptée', 'Gestion du stress et relaxation'].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-pink-100 rounded-full">
                      <i className="ri-heart-fill text-pink-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://wa.me/243850465500"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Prendre rendez-vous</span>
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/5a3959b32bb725079b06a3e0e0f7c132.jpg"
                alt="Maternity Zen"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Nos services Maternity Zen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un programme complet pour accompagner chaque future maman
            </p>
          </motion.div>

          <div className="space-y-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border-2 border-gray-100 rounded-3xl p-8 hover:shadow-xl transition-all"
              >
                <div className="flex items-start space-x-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-pink-100 rounded-2xl flex-shrink-0">
                    <i className={`${service.icon} text-4xl text-pink-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-2xl font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=happy%20pregnant%20woman%20doing%20gentle%20prenatal%20yoga%20exercises%2C%20maternal%20wellness%2C%20pregnancy%20fitness%2C%20peaceful%20atmosphere%2C%20professional%20guidance%2C%20clean%20modern%20studio%20with%20soft%20pink%20accents&width=700&height=500&seq=maternity2&orientation=landscape"
                alt="Bienfaits Maternity Zen"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover object-top"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="font-serif text-4xl font-bold text-gray-900">
                Les bienfaits pour vous et votre bébé
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Notre programme Maternity Zen offre de nombreux avantages pour votre santé et celle de votre bébé tout au long de la grossesse.
              </p>
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-pink-100 rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-pink-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700 leading-relaxed">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-primary to-primary-dark text-white rounded-3xl p-12 text-center"
          >
            <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
              <i className="ri-shield-check-line text-4xl text-white"></i>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold mb-4">
              Votre sécurité, notre priorité
            </h2>
            <p className="text-xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Tous nos protocoles sont validés médicalement et nos spécialistes sont certifiés en périnatalité. 
              Nous travaillons en étroite collaboration avec votre médecin pour garantir un accompagnement sûr et adapté à chaque étape de votre grossesse.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-pink-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <h2 className="font-serif text-4xl lg:text-5xl font-bold text-gray-900">
              Vivez votre grossesse en toute sérénité
            </h2>
            <p className="text-xl text-gray-600">
              Réservez votre première séance Maternity Zen dès aujourd'hui
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/243850465500"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-semibold transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Prendre rendez-vous</span>
              </a>
            </div>
            <p className="text-gray-600">
              <i className="ri-time-line mr-2"></i>
              Réponse sous 2h via WhatsApp
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MaternityZenPage;